import React, { useState } from 'react';
import { Solicitud } from '@/types/solicitud';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { useAuth } from '@/hooks/useAuth';

// ── Helpers de formato ──────────────────────────────────────────────────────
const fmt = (v: unknown): string => {
  if (v == null || v === '') return '—';
  return String(v);
};
const fmtCurrency = (v: unknown): string => {
  const n = Number(v);
  if (v == null || v === '' || isNaN(n)) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n);
};
const fmtDate = (v: unknown): string => {
  if (!v || String(v).trim() === '') return '—';
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── Subcomponente: fila de dato de solo lectura ──────────────────────────────
const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</span>
    <span className="text-sm text-gray-800 font-medium break-words">{value}</span>
  </div>
);

// Campos de revisión documental (label, clave backend). Orden como en pantalla.
export const REVISION_DOCUMENTAL_FIELDS: { label: string; key: string }[] = [
  { label: 'Solicitud', key: 'solicitudDeCredito' },
  { label: 'Solicitud de Seguro', key: 'solcitudDeSeguro' },
  { label: 'Identificación Oficial', key: 'identificacionOficial' },
  { label: 'Validación INE', key: 'validacionINE' },
  { label: 'CURP', key: 'formatoCURP' },
  { label: 'Comprobante de Domicilio', key: 'comprobanteDomicilio' },
  { label: 'Validación de Sueldos', key: 'validacionSueldos' },
  { label: 'Estado de Cuenta', key: 'estadoDeCuenta' },
  { label: 'Listas Negras (CN y AN-001)', key: 'listasNegras' },
  { label: 'Buro de Credito (Sólo en CN)', key: 'solicitudBuroCredito' },
];

// Helper para leer valor de revisión documental
export const getRevisionDocumentalValue = (s: Solicitud, key: string): string => {
  const fromRd = s.revisionDocumental?.[key];
  if (fromRd != null) return String(fromRd);
  const fromTop = (s as unknown as Record<string, unknown>)[key];
  return fromTop == null ? '' : String(fromTop);
};

// Estatus por tipo de campo
export const getRevisionDocumentalOptions = (key: string): { value: string; label: string }[] => {
  if (key === 'solicitudDeCredito') {
    return [{ value: 'Ok', label: 'Ok' }, { value: 'No Aplica', label: 'No aplica' }];
  }
  if (key === 'validacionSueldos') {
    return [
      { value: 'Ok', label: 'Ok' },
      { value: 'No Cumple', label: 'No cumple' },
      { value: 'Excepción documentada', label: 'Excepción documentada' },
    ];
  }
  if (key === 'listasNegras') {
    return [
      { value: 'Ok', label: 'Ok' },
      { value: 'No Cumple', label: 'No cumple' },
      { value: 'No Aplica', label: 'No aplica' },
      { value: 'Excepción documentada', label: 'Excepción documentada' },
    ];
  }
  return [
    { value: 'Ok', label: 'Ok' },
    { value: 'No Cumple', label: 'No cumple' },
    { value: 'No Aplica', label: 'No aplica' },
    { value: 'Excepción documentada', label: 'Excepción documentada' },
  ];
};

interface RevisionDocumentalSectionProps {
  localSolicitud: Solicitud;
  setLocalSolicitud: React.Dispatch<React.SetStateAction<Solicitud | null>>;
  setHasChanges: (v: boolean) => void;
  error: string | null;
  successMsg: string | null;
  isSavingRevision: boolean;
  handleSaveRevision: () => Promise<void>;
  toggleDateField: (field: keyof Solicitud, enabled: boolean) => Promise<void>;
  /** Si es true, oculta los datos del cliente y comentarios (se muestran en otro lugar) */
  hideClientData?: boolean;
}

const ACL_REVISION_KEYS = [
  'solicitudDeCredito', 'solcitudDeSeguro',
  'identificacionOficial', 'validacionINE', 'formatoCURP', 'comprobanteDomicilio',
  'validacionSueldos', 'estadoDeCuenta', 'listasNegras', 'solicitudBuroCredito',
];

export const RevisionDocumentalSection: React.FC<RevisionDocumentalSectionProps> = ({
  localSolicitud, setLocalSolicitud, setHasChanges, error, successMsg, isSavingRevision, handleSaveRevision, toggleDateField, hideClientData = false,
}) => {
  const { isFieldAllowed } = useAuth();
  const [showDatosCliente, setShowDatosCliente] = useState(true);
  const canListaArchivos = isFieldAllowed('listaArchivos');
  const canGuardarDocumentos = isFieldAllowed('accionGuardarDocumentos');
  const canEditFechaRevisionDocumentalConluida = isFieldAllowed('fechaRevisionDocumentalConluida');
  const canSeeComentarios = isFieldAllowed('comentarios');
  const fieldsToShow = REVISION_DOCUMENTAL_FIELDS.filter(({ key }) =>
    ACL_REVISION_KEYS.includes(key) ? isFieldAllowed(key) : true
  );

  const s = localSolicitud;
  const nombreCompleto = [s.clienteNombre, s.clienteApellidoPaterno, s.clienteApellidoMaterno]
    .filter(Boolean).join(' ') || '—';

  return (
    <>
      {error && (
        <div className="p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="p-3 rounded border border-green-200 bg-green-50 text-green-700 text-sm font-medium">
          {successMsg}
        </div>
      )}

      {/* ── Panel: Datos del Cliente cargados por el Promotor ── */}
      {!hideClientData && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowDatosCliente(v => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-blue-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">
              Datos del Cliente — cargados por el Promotor
            </span>
            <span className="text-[10px] bg-blue-100 text-blue-600 border border-blue-300 rounded px-1.5 py-0.5 font-medium">
              Solo lectura
            </span>
          </div>
          <svg
            className={`w-4 h-4 text-blue-500 transition-transform ${showDatosCliente ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showDatosCliente && (
          <div className="px-4 pb-4 space-y-4 border-t border-blue-200">

            {/* Identificación */}
            <div>
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mt-3 mb-2 border-b border-blue-200 pb-1">
                Identificación
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <InfoRow label="Nombre completo" value={nombreCompleto} />
                <InfoRow label="Empresa" value={fmt(s.clienteEmpresa)} />
                <InfoRow label="Puesto" value={fmt(s.clientePuesto)} />
                <InfoRow label="Tipo" value={fmt(s.clienteNuevoRenovacion)} />
                <InfoRow label="Antigüedad" value={fmt(s.clienteAntiguedad)} />
                <InfoRow label="Fecha de ingreso" value={fmtDate(s.clienteFechaIngreso)} />
                <InfoRow label="Directivo" value={s.clienteDirectivo ? 'Sí' : s.clienteDirectivo === false ? 'No' : '—'} />
                <InfoRow label="Sindicalizado" value={fmt(s.clienteSindicalizado)} />
                <InfoRow label="¿Cómo se enteró?" value={fmt(s.clienteSeEnteroPor)} />
              </div>
            </div>

            {/* Contacto */}
            <div>
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 border-b border-blue-200 pb-1">
                Contacto
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <InfoRow label="Correo cliente" value={fmt(s.clienteCorreo)} />
                <InfoRow label="Nombre del jefe" value={fmt(s.clienteNombreJefe)} />
                <InfoRow label="Correo del jefe" value={fmt(s.clienteCorreoJefe)} />
                <InfoRow label="CLABE" value={fmt(s.clienteClabe)} />
              </div>
            </div>

            {/* Datos Financieros */}
            <div>
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 border-b border-blue-200 pb-1">
                Datos Financieros
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <InfoRow label="Salario" value={fmtCurrency(s.clienteSalario)} />
                <InfoRow label="Frecuencia de pago" value={fmt(s.frecuenciaDePago)} />
                <InfoRow label="Deducciones por periodo" value={fmtCurrency(s.clienteDeduccionesPeriodo)} />
                <InfoRow label="Deducciones semanales" value={fmtCurrency(s.clienteDeduccionesSemanales)} />
                <InfoRow label="Deducciones quincenales" value={fmtCurrency(s.clienteDeduccionesQuincenales)} />
                <InfoRow label="Deducciones mensuales" value={fmtCurrency(s.clienteDeduccionesMensuales)} />
              </div>
            </div>

            {/* Solicitud */}
            <div>
              <p className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-2 border-b border-blue-200 pb-1">
                Solicitud
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <InfoRow label="Número de control" value={fmt(s.solicitudNumeroControl)} />
                <InfoRow label="Producto" value={fmt(s.producto)} />
                <InfoRow label="Monto solicitado" value={fmtCurrency(s.montoSolicitado)} />
                <InfoRow label="Plazo solicitado" value={fmt(s.plazoSolicitado)} />
                <InfoRow label="Destino del crédito" value={fmt(s.solicitudDestinoCredito)} />
                <InfoRow label="Asesor" value={fmt(s.asesorNombre)} />
                <InfoRow label="Región" value={fmt(s.regionNombre)} />
                <InfoRow label="Fecha contacto" value={fmtDate(s.fechaContacto)} />
                <InfoRow label="Fecha entrega a riesgos" value={fmtDate(s.fechaEntregaARiesgos)} />
              </div>
            </div>

          </div>
        )}
      </div>
      )}

      {!hideClientData && canSeeComentarios && (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Comentarios</p>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{localSolicitud.comentarios?.trim() || '— Sin comentarios'}</p>
      </div>
      )}
      <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-4 space-y-4 shadow-md">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-emerald-300 pb-2">
          <h3 className="text-lg font-semibold text-emerald-800">Revisión Documental</h3>
          {canListaArchivos && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => localSolicitud.documentos && window.open(localSolicitud.documentos, '_blank')}
              disabled={!localSolicitud.documentos}
            >
              {localSolicitud.documentos ? 'Ver documentos' : 'Sin enlace a documentos'}
            </Button>
          )}
        </div>
        <div className="grid gap-3">
          {fieldsToShow.map(({ label, key }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <label className="text-sm font-medium text-emerald-800 shrink-0">{label}</label>
              <select
                className="flex-1 max-w-[180px] px-3 py-1.5 border border-emerald-300 rounded-lg bg-white text-sm text-emerald-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                value={getRevisionDocumentalValue(localSolicitud, key)}
                onChange={(e) => {
                  const value = e.target.value;
                  setLocalSolicitud((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      revisionDocumental: {
                        ...(prev.revisionDocumental || {}),
                        [key]: value,
                      },
                    };
                  });
                  setHasChanges(true);
                }}
              >
                <option value="">Seleccionar...</option>
                {getRevisionDocumentalOptions(key).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
        {canGuardarDocumentos && (
        <div className="flex justify-end pt-2">
          <Button
            variant="secondary"
            onClick={handleSaveRevision}
            disabled={isSavingRevision}
          >
            {isSavingRevision ? 'Guardando...' : 'Guardar Revisión'}
          </Button>
        </div>
        )}
        {canEditFechaRevisionDocumentalConluida && (
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-emerald-300">
          <span className="text-sm font-medium text-emerald-800">Revisión Documental Concluída</span>
          <Switch
            checked={!!localSolicitud.fechaRevisionDocumentalConluida}
            onChange={(checked) => toggleDateField('fechaRevisionDocumentalConluida' as keyof Solicitud, checked)}
            color="green"
          />
        </div>
        )}
      </div>
    </>
  );
};
